import { GQLContentPageFragment } from '@theprintbar/graphql';
import { BrandColours, ImageSize, cn } from '@theprintbar/shared';
import { ButtonSize, OutlineLinkButton, PromoImageContainer, SmallArrowIcon } from '@theprintbar/ui';
import { CTAContainer } from './CTAContainer';
import { CTAText } from './CTAText';

export interface CTAProps {
	cta?: GQLContentPageFragment['fullCTA'];
	rounded?: boolean;
	hero?: boolean;
	reversed?: boolean;
	imageClassName?: string;
	//Defaults to lazy unless this is the hero, which is above the fold and usually the LCP element
	lazyLoad?: boolean;
}

export const FullCTATitleClass = (title?: string | null) =>
	cn(
		!!title && title.length > 20 ? 'text-[40px]' : 'text-[50px]',
		'lg:text-[80px] uppercase font-gravity font-black max-w-2xl leading-[45px] lg:leading-[65px]',
	);

export function FullCTA({ cta, rounded, reversed, hero, imageClassName, lazyLoad = !hero }: CTAProps) {
	if (!cta?.structure) return null;

	const { title, text, colour, icon, image, links, sticker } = cta.structure;

	return (
		<CTAContainer isFull stickerPosition="bottom-middle" sticker={sticker}>
			{/* reverse properties should be careful here */}
			<div
				className={cn('flex flex-col-reverse lg:flex-row', reversed && 'lg:flex-row-reverse flex-col')}
				style={{ backgroundColor: colour || BrandColours.Yellow }}
			>
				{/* CTA Container */}
				<div
					className={cn(
						'lg:py-36 py-10 lg:w-[50%] px-4 lg:px-28 xl:pr-48 flex flex-col justify-center max-h-[80vh]',
						sticker && 'lg:pb-[280px]',
						rounded && !hero && 'lg:items-center ',
					)}
				>
					{/* Content */}
					<div className={cn('space-y-4 lg:space-y-10')}>
						<h2 className={cn(FullCTATitleClass(title), rounded && 'normal-case font-display font-light text-2xl lg:text-3xl')}>
							{title}
						</h2>
						<CTAText useH3={hero} text={text} className="text-base 2xl:text-lg" />
						{links?.map(link =>
							link ? (
								<OutlineLinkButton
									key={link.text}
									className="w-fit uppercase font-belfast !font-bold text-xs"
									to={link.to || '#'}
									icon={SmallArrowIcon}
									iconSize={ButtonSize.S}
								>
									{link.text}
								</OutlineLinkButton>
							) : null,
						)}
					</div>
				</div>
				{/* Image */}
				<div className={cn('lg:w-[50%] max-h-[80vh]', rounded && 'px-4 lg:px-8 py-8', rounded && !hero && 'lg:py-20')}>
					<PromoImageContainer
						promoImage={image}
						size={ImageSize.HD}
						lazyLoad={lazyLoad}
						sizes="(min-width: 1024px) 50vw, 100vw"
						className={cn('w-full h-full aspect-[4/3]', rounded && 'rounded-2xl overflow-hidden', imageClassName)}
					/>
				</div>
			</div>
		</CTAContainer>
	);
}
